import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { CaseStatus, KNOWN_ISSUES } from '~/pages/CommercialOps/AriAnomalies/types';

import {
  CaseDetail,
  CasePage,
  CaseQuery,
  RollupDimension,
  TransitionBody,
  getCaseDetail,
  getCaseSummary,
  getRollup,
  listCases,
  listKnownIssues,
  listRules,
  transitionCase,
} from '~/services/AriAnomaliesService';

export const ariAnomaliesQueryKeys = {
  all: ['reporting', 'ari-anomalies'] as const,
  cases: () => [...ariAnomaliesQueryKeys.all, 'cases'] as const,
  caseList: (query: CaseQuery) => [...ariAnomaliesQueryKeys.cases(), query] as const,
  summary: () => [...ariAnomaliesQueryKeys.all, 'summary'] as const,
  rollups: () => [...ariAnomaliesQueryKeys.all, 'rollup'] as const,
  rollup: (dimension: RollupDimension) => [...ariAnomaliesQueryKeys.rollups(), dimension] as const,
  details: () => [...ariAnomaliesQueryKeys.all, 'detail'] as const,
  detail: (caseId: string) => [...ariAnomaliesQueryKeys.details(), caseId] as const,
  knownIssues: () => [...ariAnomaliesQueryKeys.all, 'known-issues'] as const,
  rules: () => [...ariAnomaliesQueryKeys.all, 'rules'] as const,
};

export function useGetAriCases(query: CaseQuery) {
  return useQuery({
    queryKey: ariAnomaliesQueryKeys.caseList(query),
    queryFn: ({ signal }) => listCases(query, signal),
    placeholderData: keepPreviousData,
  });
}

export function useGetAriCaseSummary() {
  return useQuery({
    queryKey: ariAnomaliesQueryKeys.summary(),
    queryFn: ({ signal }) => getCaseSummary(signal),
  });
}

export function useGetAriRollup(dimension: RollupDimension) {
  return useQuery({
    queryKey: ariAnomaliesQueryKeys.rollup(dimension),
    queryFn: ({ signal }) => getRollup(dimension, signal),
    placeholderData: keepPreviousData,
  });
}

export function useGetAriRules() {
  return useQuery({
    queryKey: ariAnomaliesQueryKeys.rules(),
    queryFn: ({ signal }) => listRules(signal),
  });
}

export function useGetAriCaseDetail(caseId: string | null) {
  return useQuery({
    queryKey: ariAnomaliesQueryKeys.detail(caseId ?? ''),
    queryFn: ({ signal }) => getCaseDetail(caseId, signal),
    enabled: !!caseId,
  });
}

export function useGetAriKnownIssues() {
  return useQuery({
    queryKey: ariAnomaliesQueryKeys.knownIssues(),
    queryFn: ({ signal }) => listKnownIssues(signal),
    placeholderData: KNOWN_ISSUES,
  });
}

interface TransitionArgs {
  caseId: string;
  status: CaseStatus;
  body?: TransitionBody;
}

export function useTransitionAriCase() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ caseId, status, body }: TransitionArgs) => transitionCase(caseId, status, body),
    onSuccess: (updated) => {
      queryClient.setQueriesData<CasePage>(
        { queryKey: ariAnomaliesQueryKeys.cases() },
        (page) =>
          page && {
            ...page,
            cases: page.cases.map((c) =>
              c.caseId === updated.caseId ? { ...c, status: updated.status, dismissReason: updated.dismissReason } : c,
            ),
          },
      );
      [ariAnomaliesQueryKeys.cases(), ariAnomaliesQueryKeys.summary(), ariAnomaliesQueryKeys.rollups()].forEach(
        (queryKey) => queryClient.invalidateQueries({ queryKey }),
      );
    },
  });
}

export type { CaseDetail };
